import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Tooltip,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Add from "@mui/icons-material/Add";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Close from "@mui/icons-material/Close";
import Code from "@mui/icons-material/Code";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import { VARIAVEIS_CONTEXTO, getVariablesByGroup } from "./chatbotContextVariables";
import {
  WHATSAPP_REPLY_BUTTON_TITLE_MAX,
  validateChatbotButtonLabels,
} from "./chatbotWhatsAppLimits";
import { useChatbotActions } from "../../hooks/useChatbot";
import { useS3Upload } from "../../hooks/useS3Upload";

function parseCondicoesString(str) {
  if (!str) return [];
  const arrCond = String(str).toLowerCase().split(",").map((s) => s.trim()).filter(Boolean);
  const variaveis = [];
  const operadores = [];
  const valores = [];
  const andor = [];
  arrCond.forEach((c) => {
    const [k, v] = c.split(":");
    if (!k) return;
    if (k === "variavel") variaveis.push(v || "");
    else if (k === "operadores") operadores.push(v || "");
    else if (k === "valor") valores.push(v || "");
    else if (k === "andor") andor.push(v || "");
  });
  const items = [];
  const max = Math.max(variaveis.length, operadores.length, valores.length);
  for (let i = 0; i < max; i += 1) {
    items.push({
      variavel: variaveis[i] || "",
      operador: operadores[i] || "igual",
      valor: valores[i] || "",
      andOr: andor[i] || (i === 0 ? "" : "and"),
    });
  }
  return items;
}

function buildCondicoesString(items) {
  const parts = [];
  items.forEach((item, idx) => {
    const v = (item.variavel || "").trim();
    const op = item.operador || "";
    const val = String(item.valor ?? "").trim();
    const andOr = item.andOr || (idx === 0 ? "" : "and");
    if (!v || !op) return;
    parts.push(`variavel:${v}`, `operadores:${op}`, `valor:${val}`);
    if (andOr) parts.push(`andor:${andOr}`);
  });
  return parts.join(",");
}

function ConditionsEditor({ form, setForm }) {
  const [rows, setRows] = useState(() => parseCondicoesString(form.condicoes));

  useEffect(() => {
    setRows(parseCondicoesString(form.condicoes));
  }, [form.condicoes]);

  useEffect(() => {
    const str = buildCondicoesString(rows);
    setForm((f) => ({ ...f, condicoes: str }));
  }, [rows, setForm]);

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        variavel: "",
        operador: "igual",
        valor: "",
        andOr: prev.length ? "and" : "",
      },
    ]);
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const operadores = [
    { value: "igual", label: "Igual" },
    { value: "menor", label: "Menor" },
    { value: "maior", label: "Maior" },
    { value: "menorigual", label: "Menor ou igual" },
    { value: "maiorigual", label: "Maior ou igual" },
    { value: "comecacom", label: "Começa com" },
    { value: "terminacom", label: "Termina com" },
    { value: "contem", label: "Contém" },
  ];

  const { byGroup, groupLabels } = getVariablesByGroup();
  const variableOptions = Object.entries(byGroup).flatMap(([groupKey, vars]) =>
    vars.map((v) => ({
      name: v.name,
      label: v.label,
      group: groupLabels[groupKey] || groupKey,
    }))
  );

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        Monte aqui as regras que serão avaliadas sobre o contexto (ex.: <code>datadigitada</code>, <code>horadigitada</code>, variáveis de cliente etc).
      </Typography>
      <Stack spacing={1.5}>
        {rows.map((row, idx) => (
          <Box key={idx} sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {idx > 0 && (
              <FormControl size="small" sx={{ minWidth: 90 }}>
                <InputLabel>Lógico</InputLabel>
                <Select
                  label="Lógico"
                  value={row.andOr || "and"}
                  onChange={(e) => updateRow(idx, { andOr: e.target.value })}
                >
                  <MenuItem value="and">E (AND)</MenuItem>
                  <MenuItem value="or">Ou (OR)</MenuItem>
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
              <InputLabel>Variável</InputLabel>
              <Select
                label="Variável"
                value={row.variavel}
                onChange={(e) => updateRow(idx, { variavel: e.target.value })}
              >
                <MenuItem value="">
                  <em>Selecione...</em>
                </MenuItem>
                {variableOptions.map((opt) => (
                  <MenuItem key={opt.name} value={opt.name}>
                    <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "baseline" }}>
                        {opt.group ? (
                          <Typography variant="caption" color="text.secondary">
                            [{opt.group}]
                          </Typography>
                        ) : null}
                        <Typography variant="body2">{opt.label}</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {opt.name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Operador</InputLabel>
              <Select
                label="Operador"
                value={row.operador}
                onChange={(e) => updateRow(idx, { operador: e.target.value })}
              >
                {operadores.map((op) => (
                  <MenuItem key={op.value} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Valor"
              value={row.valor}
              onChange={(e) => updateRow(idx, { valor: e.target.value })}
              sx={{ minWidth: 120, flex: 1 }}
            />
            <IconButton size="small" color="error" onClick={() => removeRow(idx)}>
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={addRow}
          sx={{ alignSelf: "flex-start", textTransform: "none" }}
        >
          Adicionar condição
        </Button>
      </Stack>
    </Box>
  );
}

function parseIdentificadores(str) {
  if (!str) return [];
  return String(str)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function IdentificadoresInput({ value, onChange, disabled }) {
  const [input, setInput] = useState("");
  const chips = useMemo(() => parseIdentificadores(value), [value]);

  const commitValue = useCallback(() => {
    const val = input.trim();
    if (!val) return;
    if (chips.includes(val)) {
      setInput("");
      return;
    }
    const next = [...chips, val];
    onChange(next.join(","));
    setInput("");
  }, [input, chips, onChange]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitValue();
    } else if (e.key === "Backspace" && !input && chips.length) {
      // backspace remove o último chip
      const next = chips.slice(0, -1);
      onChange(next.join(","));
    }
  };

  return (
    <Box>
      <TextField
        label="Identificador"
        size="small"
        fullWidth
        disabled={disabled}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commitValue}
        placeholder="Digite e pressione Enter para adicionar"
        variant="outlined"
      />
      <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {chips.map((chip) => (
          <Chip
            key={chip}
            label={chip}
            onDelete={
              disabled
                ? undefined
                : () => {
                    const next = chips.filter((c) => c !== chip);
                    onChange(next.join(","));
                  }
            }
            size="small"
          />
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
        Texto ou opções (separe por vírgula) que ativam este passo.
      </Typography>
    </Box>
  );
}

export default function ChatbotNodePanel({
  slot,
  onClose,
  onSave,
  onDelete,
  onAddSubpasso,
  isSaving,
  isDeleting,
  isAddingSubpasso = false,
  saveError,
  canDelete = true,
}) {
  const [form, setForm] = useState({
    identificador: "",
    nome: "",
    resposta: "",
    botoes: [],
    condicoes: "",
    tipo_acao: 0,
    id_acao: 0,
    global: false,
    ignorarglobal: false,
    horario_seg_ini: "",
    horario_seg_fim: "",
    horario_ter_ini: "",
    horario_ter_fim: "",
    horario_qua_ini: "",
    horario_qua_fim: "",
    horario_qui_ini: "",
    horario_qui_fim: "",
    horario_sex_ini: "",
    horario_sex_fim: "",
    horario_sab_ini: "",
    horario_sab_fim: "",
    horario_dom_ini: "",
    horario_dom_fim: "",
  });
  const [touched, setTouched] = useState({ global: false, ignorarglobal: false });
  const [localSaveError, setLocalSaveError] = useState(null);

  useEffect(() => {
    if (!slot) return;
    const hasGlobal = slot.global !== null && slot.global !== undefined;
    const hasIgnorarGlobal = slot.ignorarglobal !== null && slot.ignorarglobal !== undefined;
    setForm({
      identificador: slot.identificador ?? "",
      nome: slot.nome ?? "",
      resposta: slot.resposta ?? "",
      botoes: Array.isArray(slot.botoes) ? [...slot.botoes] : (slot.botoes ? String(slot.botoes).split(",").map((b) => b.trim()).filter(Boolean) : []),
      condicoes: slot.condicoes ?? "",
      tipo_acao: slot.tipo_acao ?? 0,
      id_acao: slot.id_acao ?? 0,
      global: hasGlobal ? !!slot.global : false,
      ignorarglobal: hasIgnorarGlobal ? !!slot.ignorarglobal : false,
      horario_seg_ini: slot.horario_seg_ini || "",
      horario_seg_fim: slot.horario_seg_fim || "",
      horario_ter_ini: slot.horario_ter_ini || "",
      horario_ter_fim: slot.horario_ter_fim || "",
      horario_qua_ini: slot.horario_qua_ini || "",
      horario_qua_fim: slot.horario_qua_fim || "",
      horario_qui_ini: slot.horario_qui_ini || "",
      horario_qui_fim: slot.horario_qui_fim || "",
      horario_sex_ini: slot.horario_sex_ini || "",
      horario_sex_fim: slot.horario_sex_fim || "",
      horario_sab_ini: slot.horario_sab_ini || "",
      horario_sab_fim: slot.horario_sab_fim || "",
      horario_dom_ini: slot.horario_dom_ini || "",
      horario_dom_fim: slot.horario_dom_fim || "",
    });
    setTouched({ global: hasGlobal, ignorarglobal: hasIgnorarGlobal });
    setLocalSaveError(null);
  }, [slot]);

  const acaoValue = `${form.tipo_acao}-${form.id_acao}`;
  const setAcaoValue = (v) => {
    const [t, a] = String(v).split("-").map(Number);
    setForm((f) => ({ ...f, tipo_acao: t || 0, id_acao: a || 0 }));
  };

  const addBotao = () => {
    setLocalSaveError(null);
    setForm((f) => ({ ...f, botoes: [...(f.botoes || []), ""] }));
  };
  const removeBotao = (i) => {
    setLocalSaveError(null);
    setForm((f) => ({ ...f, botoes: f.botoes.filter((_, j) => j !== i) }));
  };
  const changeBotao = (i, v) => {
    setLocalSaveError(null);
    const clipped = String(v).slice(0, WHATSAPP_REPLY_BUTTON_TITLE_MAX);
    setForm((f) => ({ ...f, botoes: f.botoes.map((b, j) => (j === i ? clipped : b)) }));
  };

  const respostaInputRef = useRef(null);
  const lastRespostaSelectionRef = useRef({ start: 0, end: 0 });

  const handleInsertVariable = useCallback(
    (varName) => {
      const input = respostaInputRef.current;
      let start = lastRespostaSelectionRef.current.start;
      let end = lastRespostaSelectionRef.current.end;
      if (input && document.activeElement === input) {
        start = input.selectionStart ?? start;
        end = input.selectionEnd ?? end;
      }
      const current = form.resposta || "";
      const newValue = current.slice(0, start) + varName + current.slice(end);
      setForm((f) => ({ ...f, resposta: newValue }));
      lastRespostaSelectionRef.current = { start: start + varName.length, end: start + varName.length };
      setTimeout(() => {
        if (respostaInputRef.current) {
          respostaInputRef.current.focus();
          respostaInputRef.current.setSelectionRange(
            lastRespostaSelectionRef.current.start,
            lastRespostaSelectionRef.current.end
          );
        }
      }, 0);
    },
    [form.resposta]
  );

  const [showVars, setShowVars] = useState(false);
  const { byGroup, groupLabels } = getVariablesByGroup();
  const { data: acoesUra = [], isLoading: isLoadingAcoes } = useChatbotActions(slot?.usuario || slot?.user_id || null);
  const selectedActionMeta = (acoesUra || []).find((a) => a.value === acaoValue);
  const { uploadOne } = useS3Upload();
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleSave = () => {
    if (!slot) return;
    const botoesErr = validateChatbotButtonLabels(form.botoes);
    if (botoesErr) {
      setLocalSaveError(botoesErr);
      return;
    }
    setLocalSaveError(null);
    const payload = {
      id: slot.id,
      ...form,
    };

    // Não define Global/Ignorar Global automaticamente em subpassos novos:
    // só envia esses campos se o usuário mexeu neles, ou se já vinham definidos no slot.
    if (!touched.global) delete payload.global;
    if (!touched.ignorarglobal) delete payload.ignorarglobal;

    onSave(payload);
  };

  const handleSelectFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    try {
      setIsUploadingFile(true);
      const prefix = `workspace-chatbot/ura/${slot?.usuario || "default"}`;
      const { url } = await uploadOne(file, { prefix });
      setForm((f) => ({ ...f, arquivo: url }));
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.message || "Falha ao enviar arquivo.");
    } finally {
      setIsUploadingFile(false);
      event.target.value = "";
    }
  };

  const handleDelete = () => {
    if (!slot || !canDelete) return;
    setConfirmDeleteOpen(true);
  };

  if (!slot) return null;

  const isExcecao = slot.identificador === "Exceção";

  return (
    <Drawer
      anchor="right"
      open={!!slot}
      onClose={onClose}
      ModalProps={{
          sx: { zIndex: 1250 },
        slotProps: {
          backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.65)" } },
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480 },
          maxWidth: "100vw",
          height: "100vh",
          maxHeight: "100vh",
          boxShadow: (t) => t.shadows[8],
          display: "flex",
          flexDirection: "column",
          // Evita contraste ruim quando o tema força Drawer escuro (ex.: Scania Claro) com palette light:
          // TextField/labels usam cores de texto para fundo claro.
          bgcolor: "background.paper",
          color: "text.primary",
        },
      }}
    >
      {/* Cabeçalho fixo: título, adicionar subpasso (sem fechar o painel) e fechar */}
      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          pt: 2,
          pb: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: isExcecao ? "warning.light" : "background.paper",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {isExcecao ? "Slot de Exceção" : "Editar passo"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {onAddSubpasso && (
            <Button
              variant="contained"
              size="small"
              startIcon={isAddingSubpasso ? <CircularProgress size={16} color="inherit" /> : <Add />}
              onClick={onAddSubpasso}
              disabled={isAddingSubpasso}
              sx={{ textTransform: "none" }}
            >
              Adicionar subpasso
            </Button>
          )}
          <IconButton onClick={onClose} size="small" aria-label="Fechar" sx={{ ml: 0.5 }}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* Conteúdo rolável com padding generoso */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: 3,
          py: 2.5,
          pb: 4,
          display: "flex",
          flexDirection: "column",
          gap: 0,
        }}
      >
        {(saveError || localSaveError) && (
          <Alert severity="error" onClose={() => {}} sx={{ mb: 2 }}>
            {saveError || localSaveError}
          </Alert>
        )}

        {isExcecao && (
          <Alert severity="info" sx={{ mb: 2 }} icon={false}>
            <Typography variant="body2" component="span">
              Este slot é acionado <strong>quando, pelas regras definidas, não for possível entrar em nenhum outro slot</strong> neste nível (ex.: mensagem fora das opções, condição não atendida). Configure aqui a resposta e os botões que o usuário verá nesse caso — por exemplo: &quot;Por favor, escolha uma opção válida&quot;.
            </Typography>
          </Alert>
        )}

        <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.8 }}>
          {isExcecao ? "Identificador (fixo)" : "Identificação"}
        </Typography>
        <Stack spacing={2}>
          {isExcecao ? (
            <TextField
              label="Identificador"
              size="small"
              fullWidth
              value={form.identificador}
              helperText='Sempre "Exceção". Não é acionado por texto; apenas quando nenhuma outra opção for válida.'
              disabled
              variant="outlined"
            />
          ) : (
            <IdentificadoresInput
              value={form.identificador}
              onChange={(newValue) => setForm((f) => ({ ...f, identificador: newValue }))}
              disabled={false}
            />
          )}
          {!isExcecao && (
            <TextField
              label="Nome (relatórios)"
              size="small"
              fullWidth
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              variant="outlined"
            />
          )}
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.8 }}>
          {isExcecao ? "Mensagem quando nenhuma opção for válida" : "Mensagem"}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Use variáveis com $nome (ex.: $nomecliente); são substituídas pelo valor no envio. *texto* = negrito.
        </Typography>
        <Box sx={{ mb: 1 }}>
          <Button
            size="small"
            startIcon={<Code />}
            endIcon={showVars ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setShowVars((v) => !v)}
            sx={{ textTransform: "none" }}
          >
            Variáveis de contexto
          </Button>
          <Collapse in={showVars}>
            <Box sx={{ mt: 1, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
              {Object.entries(byGroup).map(([groupKey, vars]) => (
                <Box key={groupKey} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    {groupLabels[groupKey] || groupKey}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {vars.map((v) => (
                      <Tooltip key={v.name} title={`${v.label} — ${v.name}`}>
                        <Chip
                          size="small"
                          label={v.label}
                          onClick={() => handleInsertVariable(v.name)}
                          sx={{ fontSize: "0.75rem" }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              ))}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                Dica: ao passar o mouse, você vê a chave (ex.: <code>$nomecliente</code>) que será inserida na mensagem.
              </Typography>
            </Box>
          </Collapse>
        </Box>
        <TextField
          label={isExcecao ? "Resposta (ex.: Por favor, escolha uma opção válida)" : "Resposta"}
          size="small"
          fullWidth
          multiline
          rows={4}
          value={form.resposta}
          onChange={(e) => {
            const el = e.target;
            lastRespostaSelectionRef.current = { start: el.selectionStart, end: el.selectionEnd };
            setForm((f) => ({ ...f, resposta: e.target.value }));
          }}
          onBlur={(e) => {
            lastRespostaSelectionRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
          }}
          inputRef={respostaInputRef}
          variant="outlined"
          sx={{ mb: 1 }}
          placeholder={isExcecao ? "Ex.: Por favor, escolha uma opção válida do menu acima." : "Ex.: Olá *$nomecliente*! Sua data: $datadigitada"}
          InputProps={{ sx: { lineHeight: 1.5 } }}
        />

        <Typography variant="overline" color="text.secondary" sx={{ mt: 2.5, mb: 0.5, letterSpacing: 0.8 }}>
          Botões
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          WhatsApp: cada rótulo de resposta rápida tem no máximo {WHATSAPP_REPLY_BUTTON_TITLE_MAX} caracteres.
        </Typography>
        <Stack spacing={1.5}>
          {(form.botoes || []).map((b, i) => (
            <Box key={i} display="flex" gap={1} alignItems="center">
              <TextField
                size="small"
                fullWidth
                value={b}
                onChange={(e) => changeBotao(i, e.target.value)}
                placeholder="Texto do botão"
                variant="outlined"
                inputProps={{ maxLength: WHATSAPP_REPLY_BUTTON_TITLE_MAX }}
                helperText={`${String(b).length}/${WHATSAPP_REPLY_BUTTON_TITLE_MAX}`}
                FormHelperTextProps={{ sx: { textAlign: "right", mr: 0 } }}
              />
              <IconButton size="small" onClick={() => removeBotao(i)} color="error" aria-label="Remover botão">
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            variant="outlined"
            size="medium"
            startIcon={<Add />}
            onClick={addBotao}
            sx={{ alignSelf: "flex-start", mt: 0.5, textTransform: "none" }}
          >
            Adicionar botão
          </Button>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.8 }}>
          Ação
        </Typography>
        <Stack spacing={1.5}>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel>Ação</InputLabel>
            <Select
              label="Ação"
              value={acaoValue}
              onChange={(e) => setAcaoValue(e.target.value)}
              disabled={isLoadingAcoes}
            >
              {(acoesUra || []).length === 0 && !isLoadingAcoes && (
                <MenuItem value="0-0">Nenhuma</MenuItem>
              )}
              {(acoesUra || []).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedActionMeta && selectedActionMeta.description && (
            <Typography variant="caption" color="text.secondary">
              {selectedActionMeta.description}
            </Typography>
          )}
        </Stack>

        {/* Seções avançadas */}
        <Divider sx={{ my: 3 }} />
        <Button
          size="small"
          variant="text"
          onClick={() => setShowAdvanced((v) => !v)}
          endIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />}
          sx={{ alignSelf: "flex-start", textTransform: "none", mb: 1 }}
        >
          {showAdvanced ? "Ocultar opções avançadas" : "Mostrar opções avançadas"}
        </Button>
        <Collapse in={showAdvanced}>
          <Box sx={{ mt: 1 }}>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.8 }}>
              Arquivo de resposta (opcional)
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                component="label"
                disabled={isUploadingFile}
                sx={{ textTransform: "none" }}
              >
                {isUploadingFile ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                {isUploadingFile ? "Enviando..." : "Selecionar arquivo"}
                <input type="file" hidden onChange={handleSelectFile} />
              </Button>
              {form.arquivo && (
                <Tooltip title={form.arquivo}>
                  <Chip
                    label="Arquivo associado"
                    color="primary"
                    variant="outlined"
                    onDelete={() => setForm((f) => ({ ...f, arquivo: "" }))}
                  />
                </Tooltip>
              )}
            </Stack>

            <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.8 }}>
              Condições
            </Typography>
            <Box sx={{ mb: 3 }}>
              <ConditionsEditor form={form} setForm={setForm} />
            </Box>

            <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.8 }}>
              Horários por dia da semana
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
                Deixe em branco para não restringir por horário. Caso preencha, o passo só será válido dentro do intervalo em cada dia.
              </Typography>
              {[
                { keyIni: "horario_seg_ini", keyFim: "horario_seg_fim", label: "Segunda-feira" },
                { keyIni: "horario_ter_ini", keyFim: "horario_ter_fim", label: "Terça-feira" },
                { keyIni: "horario_qua_ini", keyFim: "horario_qua_fim", label: "Quarta-feira" },
                { keyIni: "horario_qui_ini", keyFim: "horario_qui_fim", label: "Quinta-feira" },
                { keyIni: "horario_sex_ini", keyFim: "horario_sex_fim", label: "Sexta-feira" },
                { keyIni: "horario_sab_ini", keyFim: "horario_sab_fim", label: "Sábado" },
                { keyIni: "horario_dom_ini", keyFim: "horario_dom_fim", label: "Domingo" },
              ].map((row) => (
                <Box
                  key={row.keyIni}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography sx={{ minWidth: 110 }} variant="body2">
                    {row.label}
                  </Typography>
                  <TextField
                    type="time"
                    size="small"
                    label="Início"
                    value={form[row.keyIni] || ""}
                    onChange={(e) => setForm((f) => ({ ...f, [row.keyIni]: e.target.value || "" }))}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 130 }}
                  />
                  <TextField
                    type="time"
                    size="small"
                    label="Fim"
                    value={form[row.keyFim] || ""}
                    onChange={(e) => setForm((f) => ({ ...f, [row.keyFim]: e.target.value || "" }))}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 130 }}
                  />
                </Box>
              ))}
            </Box>

            <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.8 }}>
              Comportamento no fluxo
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, lineHeight: 1.45 }}>
              Slots globais são acionados em qualquer nível da conversa. Use &quot;Ignorar global&quot; em um passo quando quiser que, nesse ponto, apenas os subpassos diretos sejam considerados.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
              <Tooltip
                title="Este passo poderá ser acionado em qualquer nível do fluxo (o usuário pode digitar o identificador mesmo estando em outro passo)."
                placement="top"
              >
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!form.global}
                        onChange={(e) => {
                          setTouched((t) => ({ ...t, global: true }));
                          setForm((f) => ({ ...f, global: e.target.checked }));
                        }}
                      />
                    }
                    label="Global"
                  />
                  <InfoOutlined sx={{ fontSize: 16, color: "action.active" }} />
                </Box>
              </Tooltip>
              <Tooltip
                title="Quando a conversa estiver neste passo, os slots marcados como Global não serão considerados; apenas os subpassos diretos deste nó."
                placement="top"
              >
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!form.ignorarglobal}
                        onChange={(e) => {
                          setTouched((t) => ({ ...t, ignorarglobal: true }));
                          setForm((f) => ({ ...f, ignorarglobal: e.target.checked }));
                        }}
                      />
                    }
                    label="Ignorar global"
                  />
                  <InfoOutlined sx={{ fontSize: 16, color: "action.active" }} />
                </Box>
              </Tooltip>
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Rodapé fixo com ações principais */}
      <Box
        sx={{
          flexShrink: 0,
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Button variant="contained" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <CircularProgress size={22} /> : "Salvar"}
          </Button>
          <Button variant="outlined" onClick={onClose}>
            Fechar
          </Button>
          {canDelete && !isExcecao && (
            <Button variant="outlined" color="error" onClick={handleDelete} disabled={isDeleting} sx={{ ml: { xs: 0, sm: "auto" } }}>
              {isDeleting ? <CircularProgress size={20} /> : "Excluir"}
            </Button>
          )}
        </Stack>
      </Box>
      {/* Confirmação de exclusão */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => !isDeleting && setConfirmDeleteOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Excluir passo do ChatBot?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Este passo faz parte do fluxo automatizado do seu ChatBot. Ao prosseguir com a exclusão,{" "}
            <strong>todos os subpassos ligados a ele também serão removidos</strong>, incluindo suas mensagens,
            botões, condições e configurações avançadas.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Se você não tiver certeza, cancele agora e revise o fluxo antes de tomar essa decisão. Caso confirme,
            a exclusão não poderá ser desfeita diretamente pela interface.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={isDeleting}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              if (!slot || !canDelete) return;
              onDelete(slot.id);
              setConfirmDeleteOpen(false);
            }}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            Excluir passo e subpassos
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
